import React, { useCallback, useEffect, useState } from "react";

import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useShopify } from "@app/scripts/react-hooks/shopify";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_PRODUCTS_BY_FILTER } from "@app/scripts/utils/queries";
import { ProductMoreColorsProduct } from "@app/scripts/react-product-more-colors/components/ProductMoreColorsProduct";

import type { NormalisedProduct, Product, Swatches } from "@app/scripts/types";
import { isUS } from "@app/scripts/utils";

const SWATCHES = JSON.parse(document.querySelector("[data-swatchesjson]")?.innerHTML) as Swatches;

type Props = {
  product: NormalisedProduct;
  related: NormalisedProduct[];
  setRelated: React.Dispatch<React.SetStateAction<NormalisedProduct[]>>;
};

export const ProductMoreColorsPlp: React.FC<Props> = ({ product, related, setRelated }) => {
  const { normaliseProduct } = useShopify();
  const colorString = isUS ? "colors" : "colours";

  const title = `${product.title.split("- ")?.[0]?.trim()}: ${related?.length} ${colorString}`;

  // grab the related products
  const query = async (dispatch: React.Dispatch<React.SetStateAction<NormalisedProduct[]>>) => {
    // "style--" is a legacy tag for backwards compatibility purposes, can be removed post 08/2025
    const styleTag = product.tags.find((tag) => tag.includes("altCode--") || tag.includes("style--"));
    if (!styleTag) return;

    let dataSet = {
      filter: "tag:" + styleTag,
    };

    const data = await shopify(GET_PRODUCTS_BY_FILTER, dataSet);

    dispatch(data?.products?.nodes?.map((p) => normaliseProduct(p)) || []);
  };

  useEffect(() => {
    query(setRelated);
  }, []);

  const handleOpenDrawer = useCallback(async (toggleOpen: () => void) => {
    toggleOpen();
  }, []);

  return (
    <>
      {related && related.length >= 1 ? (
        <Drawer
          heading={title}
          drawerClassName="product-more-colors__drawer"
          customButton={({ toggleOpen }) => (
            <div className="product-card__siblings">
              {related.slice(0, 4).map((r_product) => {
                const tag = r_product.tags.find((tag) => tag.includes("primaryColor--"));
                const bgColor = SWATCHES?.find((swatch) => swatch.tag_name === tag)?.colour;
                return bgColor ? (
                  <a href={`/products/${r_product.handle}`} aria-label={r_product.title}>
                    <span
                      className={tag === "primaryColor--white" ? "border" : ""}
                      style={{ backgroundColor: bgColor }}
                    />
                  </a>
                ) : null;
              })}
              {related.length > 4 && (
                <button onClick={() => handleOpenDrawer(toggleOpen)}>{`+${related.length}`}</button>
              )}
            </div>
          )}
        >
          <div className="product-more-colors page-width">
            {related?.map((product) => (
              <ProductMoreColorsProduct key={product.id} product={product} />
            ))}
          </div>
        </Drawer>
      ) : (
        <div className="product-card__siblings" />
      )}
    </>
  );
};

export const ProductMoreColorsPlpConvert = ({ product }: { product: Product }) => {
  const { normaliseProduct } = useShopify();
  const normalised = normaliseProduct(product);
  const [related, setRelated] = useState<NormalisedProduct[]>([]); // limit to 4 products

  return <ProductMoreColorsPlp product={normalised} related={related} setRelated={setRelated} />;
};
